import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Phone,
  ChevronDown,
  CheckCircle2,
  Laptop,
  Mail,
  ShieldCheck,
  Wifi,
  FileCheck,
  Package,
  Database,
  UserRound,
  Building2,
  Briefcase,
  MapPin,
  ArrowRight,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { getPostsByTag } from "@/data/blog-posts";
import { SITE_URL, canonical, seoCanonicalLink, seoOgUrl } from "@/lib/seo";

const LOCATIONS = [
  "Montclair, CA",
  "Rancho Cucamonga, CA",
  "Ontario, CA",
  "Upland, CA",
  "Claremont, CA",
  "Chino, CA",
  "Pomona, CA",
  "Inland Empire & Orange County",
];

const TITLE = "Tech Support for Insurance Agents | SimpleTech";
const DESCRIPTION =
  "Compliant, reliable tech support for insurance agents and agencies. Device management, security, and IT support from SimpleTech. Call (909) 255-9112.";
const CANONICAL = canonical("/services/tech-support-for-insurance-agents");

const FAQS = [
  {
    q: "Can you help our insurance agency meet device compliance requirements?",
    a: "Yes — compliance support is one of our specialties. SimpleTech audits your devices, identifies gaps, brings them up to standard, and sets up ongoing monitoring so your agency stays audit-ready between reviews.",
  },
  {
    q: "Do you support agency management software and carrier portals?",
    a: "Yes. SimpleTech provides troubleshooting and support for agency management systems (AMS), quoting platforms, and carrier portals so your agents stay productive and your workflows stay uninterrupted.",
  },
  {
    q: "How do you handle equipment for new hires?",
    a: "We manage the full process — procurement, configuration, and distribution. When a new agent joins your team, their device arrives ready to use on day one. No setup delays, no IT backlog.",
  },
  {
    q: "How do you protect sensitive client data at an insurance agency?",
    a: "SimpleTech implements encrypted backups, multi-factor authentication, access controls, malware protection, and staff security training — a layered approach that protects both your clients and your agency from data breaches and compliance violations.",
  },
  {
    q: "Do you offer remote support for agents who work outside the office?",
    a: "Absolutely. Most issues are resolved remotely the same day. We provide secure remote support for insurance agents whether they're in the office, at home, or meeting clients in the field.",
  },
];

export const Route = createFileRoute("/services_/tech-support-for-insurance-agents")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Tech Support for Insurance Agents",
          description:
            "Compliant, reliable tech support for insurance agents and agencies — device management, equipment distribution, data security, and remote support.",
          provider: {
            "@type": "LocalBusiness",
            name: "SimpleTech",
            telephone: "+1-909-255-9112",
            url: SITE_URL,
            address: {
              "@type": "PostalAddress",
              streetAddress: "8880 N Benson Ave #121",
              addressLocality: "Montclair",
              addressRegion: "CA",
              postalCode: "91763",
              addressCountry: "US",
            },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map(({ q, a }) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        }),
      },
    ],
  }),
  component: InsuranceTechSupportPage,
});

const SERVICES = [
  {
    Icon: FileCheck,
    title: "Device Compliance & Management",
    body: "Insurance agencies are subject to state regulations, carrier requirements, and data privacy standards that require devices to be properly configured, updated, and secured. SimpleTech audits your existing devices, brings them into compliance, and sets up ongoing monitoring so your agency is always audit-ready.",
  },
  {
    Icon: Package,
    title: "Equipment Distribution & Onboarding",
    body: "When a new agent joins your team or an existing employee needs upgraded equipment, delays cost money. SimpleTech manages procurement, configuration, and distribution of office equipment so your team has the right tools from day one — laptops, printers, phones, and everything in between.",
  },
  {
    Icon: Database,
    title: "Agency Management System (AMS) Support",
    body: "Your AMS is the backbone of your agency — it needs to work. SimpleTech provides support and troubleshooting for agency management platforms, quoting tools, and carrier portals so your agents can spend time selling coverage, not fighting software. If something breaks, we fix it fast.",
  },
  {
    Icon: ShieldCheck,
    title: "Data Security & Client Privacy Protection",
    body: "Your clients trust you with sensitive personal and financial information. A breach can expose your agency to regulatory fines, E&O claims, and reputational damage. SimpleTech implements layered security: encrypted backups, multi-factor authentication, password management, malware protection, and staff cybersecurity training.",
  },
  {
    Icon: Mail,
    title: "Email, Communication & Document Management",
    body: "We set up and support Google Workspace and Microsoft 365, configure shared team inboxes, enable calendar syncing, and organize cloud document storage so policies, applications, and client records are always accessible — and always secure. Proper document management also simplifies audits.",
  },
  {
    Icon: Wifi,
    title: "Remote Support — Help When You're in the Field",
    body: "Insurance agents aren't always at a desk. Whether you're meeting clients on-site, working from home, or running a satellite office, SimpleTech's remote support resolves most issues quickly without an in-person visit — fast, secure, and convenient for a mobile workforce.",
  },
  {
    Icon: Laptop,
    title: "Network & Office Infrastructure",
    body: "From Wi-Fi setup and printer troubleshooting to internet connectivity and VoIP phone systems, SimpleTech keeps your office infrastructure running reliably. A strong, stable network is the foundation everything else depends on — we make sure yours never lets you down.",
  },
];

const WHY_POINTS = [
  "Compliance-focused — audit-ready devices and documentation",
  "Same-day response — most issues resolved within hours",
  "Equipment procurement and onboarding handled end-to-end",
  "Layered security to protect client data and meet E&O standards",
  "Plain-English explanations — no tech jargon",
  "A real local team that picks up the phone",
];

function InsuranceTechSupportPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <SiteHeader active="services" />
      <main id="main-content" className="flex-1">
        {/* Hero */}
        <section className="relative bg-[#0b0a3d] text-white overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0b0a3d] via-[#141432] to-[#1e1e5a] opacity-95" />
          <div className="relative z-10 max-w-5xl mx-auto px-6 py-20 md:py-28">
            <p className="text-sky-400 font-bold tracking-widest text-sm uppercase mb-4">
              Insurance Agency Tech Support
            </p>
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Tech Support for Insurance Agents
            </h1>
            <p className="mt-5 text-xl md:text-2xl font-bold text-sky-400">
              Compliant. Secure. Always on.
            </p>
            <p className="mt-8 text-base md:text-lg text-white/85 leading-relaxed max-w-3xl">
              Insurance agencies run on trust — trust with clients, trust with carriers, and trust that your technology
              is secure, compliant, and working when you need it. SimpleTech provides{" "}
              <strong className="text-white">dedicated tech support for insurance agents</strong> — keeping your devices
              compliant, your data protected, and your team equipped to serve clients without interruption. No jargon,
              no delays, and no compliance headaches.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 items-center">
              <a
                href="tel:+19092559112"
                className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                <Phone className="w-4 h-4" /> CALL (909) 255-9112
              </a>
              <Link
                to="/contact"
                className="inline-block bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                REQUEST SUPPORT
              </Link>
            </div>
            <p className="mt-5 text-sm text-white/70">Local support. Fast response.</p>
          </div>
        </section>

        {/* Why */}
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
              The Technology Challenges Insurance Agencies Face
            </h2>
            <p className="mt-5 text-slate-700 text-base md:text-lg leading-relaxed max-w-3xl">
              Insurance professionals deal with technology pressures that generic IT companies aren't equipped to
              handle. Your agency management system needs to be online. Client records need to be protected. Devices
              need to meet state and carrier compliance standards. And your team needs the right equipment the moment
              they need it. When those systems break down, agents can't quote, staff can't process claims, and clients
              feel it. SimpleTech was built to make sure that never happens.
            </p>
            <ul className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-4">
              {WHY_POINTS.map((p) => (
                <li key={p} className="flex items-start gap-3 text-slate-800">
                  <CheckCircle2 className="w-5 h-5 text-sky-500 mt-0.5 shrink-0" />
                  <span className="leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Case study */}
        <section className="bg-slate-50 py-20 md:py-24">
          <div className="max-w-4xl mx-auto px-6">
            <p className="text-sky-600 font-bold tracking-widest text-sm uppercase mb-4">Real Results</p>
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
              How We Helped an Insurance Agency Get Compliant
            </h2>
            <div className="mt-6 space-y-5 text-slate-700 text-base md:text-lg leading-relaxed">
              <p>
                One of our insurance agency clients was struggling to keep their operations organized and compliant.
                Their devices needed regular checks to meet industry standards, and employees were experiencing delays
                getting the equipment they needed to do their jobs.
              </p>
              <p>
                SimpleTech stepped in with a complete solution. We audited every device in the agency, brought them
                fully up to compliance standards, and took over the distribution and management of office equipment.
                The result: every team member now has what they need, when they need it — and leadership can focus on
                growing the business instead of worrying about whether their technology passes the next compliance
                review.
              </p>
              <p className="font-semibold text-[#0b0a3d]">
                That's the SimpleTech difference. We don't just fix problems — we build systems that prevent them.
              </p>
            </div>
          </div>
        </section>

        {/* Services grid */}
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
              Tech Support Services for Insurance Agents
            </h2>
            <p className="mt-5 text-slate-700 text-base md:text-lg leading-relaxed max-w-3xl">
              SimpleTech provides end-to-end tech support for insurance agencies, covering every layer of your
              operation.
            </p>
            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {SERVICES.map(({ Icon, title, body }) => (
                <div key={title} className="bg-slate-50 rounded-lg p-7 shadow-sm hover:shadow-md transition-shadow">
                  <Icon className="w-10 h-10 text-sky-500" strokeWidth={1.5} />
                  <h3 className="mt-5 text-xl font-extrabold text-[#0b0a3d]">{title}</h3>
                  <p className="mt-3 text-slate-600 text-[15px] leading-relaxed">{body}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 text-slate-700 text-[15px] leading-relaxed max-w-3xl space-y-3">
              <p>
                Looking for a complete IT partner? See our{" "}
                <Link to="/services/managed-it-services" className="text-sky-600 font-semibold hover:underline">
                  managed IT services
                </Link>{" "}
                for end-to-end coverage, or our{" "}
                <Link
                  to="/services/$slug"
                  params={{ slug: "cyber-security" }}
                  className="text-sky-600 font-semibold hover:underline"
                >
                  cybersecurity services
                </Link>{" "}
                to protect client data and meet cyber-insurance requirements.
              </p>
              <p>
                Need a reliable phone system too? See our{" "}
                <Link
                  to="/services/$slug"
                  params={{ slug: "voip" }}
                  className="text-sky-600 font-semibold hover:underline"
                >
                  VoIP solutions for insurance offices
                </Link>
                .
              </p>
            </div>
          </div>
        </section>

        {/* Who we support */}
        <section className="bg-slate-50 py-20 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">Who We Support</h2>
            <p className="mt-5 text-slate-700 text-base md:text-lg leading-relaxed max-w-3xl">
              SimpleTech provides insurance agency tech support for:
            </p>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { Icon: UserRound, label: "Independent insurance agents" },
                { Icon: Building2, label: "Captive agency offices (State Farm, Allstate, Farmers, etc.)" },
                { Icon: Briefcase, label: "Multi-line agencies & brokerages" },
                { Icon: ShieldCheck, label: "Life, health, auto & commercial offices" },
                { Icon: FileCheck, label: "Agency support staff & operations teams" },
                { Icon: Database, label: "Claims processors & service reps" },
              ].map(({ Icon, label }) => (
                <div key={label} className="flex items-center gap-4 p-5 rounded-md border border-slate-200 bg-white">
                  <Icon className="w-8 h-8 text-sky-500 shrink-0" strokeWidth={1.5} />
                  <span className="font-semibold text-[#0b0a3d]">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Locations We Serve */}
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">Locations We Serve</h2>
            <p className="mt-5 text-slate-700 text-base md:text-lg leading-relaxed max-w-3xl">
              SimpleTech supports insurance agencies across Southern California — with same-day remote help everywhere
              we work, and on-site visits across the areas below.
            </p>
            <div className="mt-10 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {LOCATIONS.map((loc) => (
                <div
                  key={loc}
                  className="flex items-center gap-3 bg-slate-50 rounded-md border border-slate-200 px-4 py-3"
                >
                  <MapPin className="w-5 h-5 text-sky-500 shrink-0" />
                  <span className="font-semibold text-[#0b0a3d] text-sm">{loc}</span>
                </div>
              ))}
            </div>
            <p className="mt-8 text-slate-700 leading-relaxed max-w-3xl">
              Don't see your city?{" "}
              <a href="tel:+19092559112" className="text-sky-600 font-semibold hover:underline">
                Call (909) 255-9112
              </a>{" "}
              — we likely cover it too.
            </p>
          </div>
        </section>

        {/* From the Blog — auto-pulls posts tagged "insurance" */}
        <BlogSection />

        {/* CTA band */}
        <section className="bg-[#0b0a3d] text-white py-20 md:py-24">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight">
              Protect Your Agency — Get Reliable Tech Support Today
            </h2>
            <p className="mt-5 text-white/85 text-base md:text-lg leading-relaxed">
              Your agency's technology should be your competitive advantage, not a liability. Whether you need help
              getting compliant, equipping your team, or keeping your systems locked down — SimpleTech is the local IT
              partner insurance agencies trust.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a
                href="tel:+19092559112"
                className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                <Phone className="w-4 h-4" /> CALL (909) 255-9112
              </a>
              <Link
                to="/contact"
                className="inline-block bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                CONTACT US
              </Link>
            </div>
            <p className="mt-6 text-sm text-white/70">No rushing. No jargon. A team that answers the phone.</p>
          </div>
        </section>

        {/* FAQs */}
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight text-center">
              Frequently Asked Questions
            </h2>
            <div className="mt-12 space-y-3">
              {FAQS.map((f, i) => {
                const open = openFaq === i;
                return (
                  <div key={f.q} className="border border-slate-200 rounded-md overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(open ? null : i)}
                      className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-slate-50 transition-colors"
                      aria-expanded={open}
                    >
                      <span className="font-bold text-[#0b0a3d]">{f.q}</span>
                      <ChevronDown
                        className={`w-5 h-5 text-sky-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                      />
                    </button>
                    {open && <div className="px-5 pb-5 text-slate-700 leading-relaxed">{f.a}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function BlogSection() {
  const posts = getPostsByTag("insurance");
  if (posts.length === 0) return null;
  return (
    <section className="bg-slate-50 py-20 md:py-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
              Insurance Agency Tech Insights
            </h2>
            <p className="mt-4 text-slate-700 text-base md:text-lg leading-relaxed max-w-2xl">
              Guides, tips, and updates for insurance agents and agency operators — from our blog.
            </p>
          </div>
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 text-sky-600 font-bold hover:text-sky-700"
          >
            All articles <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.slice(0, 6).map((post) => (
            <Link
              key={post.slug}
              to="/articles/$slug"
              params={{ slug: post.slug }}
              className="group bg-white rounded-lg overflow-hidden border border-slate-200 hover:shadow-lg transition-shadow"
            >
              <div className="aspect-[16/9] overflow-hidden bg-slate-200">
                <img
                  src={post.coverImage}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <p className="text-xs font-bold text-sky-600 tracking-wider uppercase">{post.date}</p>
                <h3 className="mt-2 text-lg font-extrabold text-[#0b0a3d] group-hover:text-sky-600 transition-colors leading-tight">
                  {post.title}
                </h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-3">{post.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
